<script lang="ts">
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import type { Image } from "#gen/bundles/v1/common_pb.js";
	import type { AssetLoad } from "#lib/api/bundles.js";
	import Icon from "#lib/components/Icon.svelte";
	import TokenizedImage from "#lib/components/TokenizedImage.svelte";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import { getAttemptedIconMatch } from "#lib/components/flow/editor/external/advancedDestinations/utils.js";
	import type { ModuleItemProps } from "#lib/components/flow/viewer/types.js";
	import { getAssetRef, getIconUrl, getText } from "#lib/components/flow/viewer/utils.js";
	import { parseIconUrl } from "#lib/icons.js";
	import { getPrimaryIcon } from "#lib/theme.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const assets: Writable<Record<string, AssetLoad>> = getContext("assets");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");

	const { suite, module, mode, readOnly, classes, onShow }: ModuleItemProps = $props();
	const ref = $derived(getAssetRef($suite, module));
	const brandIcon = $derived($brandKit ? getPrimaryIcon($brandKit!) : (undefined as Image | undefined));
	const iconUrl = $derived(getIconUrl($assets, module.key, ref, mode));
	const text = $derived(getText($assets, module.key, ref, mode));

	function handleShow() {
		onShow?.(module);
	}
</script>

<button data-flow-button type="button" disabled={readOnly} onclick={handleShow} class={twMerge("flex h-12 w-full max-w-100 cursor-pointer items-center gap-2 rounded-2xl bg-white p-2.5 underline", classes.readOnlyClass)} class:!opacity-50={ref?.disabled}>
	<div class="flex w-full items-center justify-center gap-2 text-sm">
		<div class="flex h-8 w-8 flex-none items-center overflow-hidden rounded-lg">
			{#if iconUrl && !parseIconUrl(iconUrl)}
				<TokenizedImage provider={$tokenProvider} src={iconUrl} {mode} imgClass="h-8 w-8" badgeClass="hidden" placeholderClass="h-4 w-4" />
			{:else if brandIcon?.url}
				<TokenizedImage provider={$tokenProvider} src={brandIcon.url} {mode} imgClass="h-8 w-8" badgeClass="hidden" placeholderClass="h-4 w-4" />
			{:else}
				{@const icon = getAttemptedIconMatch(ref)}
				{#if icon}
					<Icon iconSet={icon.iconSet} name={icon.icon} class="h-6 w-6" />
				{/if}
			{/if}
		</div>
		<TokenizedText provider={$tokenProvider} {text} {mode} />
	</div>
</button>
